/** ***********************************************************************
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ************************************************************************* */

/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { Component } from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger';
import Tooltip from '@react/react-spectrum/Tooltip';
import uuid from 'uuid';
import { appTileAnalytics } from '../../../../../analytics';
import TranslationUtil from '../../../../../utils/translation';
import { interpretKey } from '../../../../../utils/eventUtil';
import {
    SUBTEXT_TYPE, ANALYTICS, LEFT_NAV_KEYS,
} from '../../../../../utils/enums';
import './index.scss';
import { ACTION_ID } from '../../../../../utils/modelMapper/appModelMapper/appModelConstants';

export default @observer class AppTileHeader extends Component {
    onTileHeaderClick(e) {
        e.stopPropagation();
        const {
            app,
            appSectionFilter,
            layoutId,
            appSectionBasedFields: {
                isCategorySection,
            },
        } = this.props;
        const guid = uuid.v4();
        app.onAppAction(ACTION_ID.APP_TILE_CLICK, app, { guid, isCategorySection });
        appTileAnalytics(ANALYTICS.EVENTS.APP_TILE_CLICK, {
            app,
            appSectionFilter,
            actionId: app.actionIds.select,
            guid,
            viewType: ANALYTICS.VIEW_TYPE[layoutId],
        });
    }

    handleIconLoadError(app, backUpIcon) {
        if (backUpIcon) {
            app.setIconUrl(backUpIcon);
        }
    }

    isUpdatesSection(appSectionFilter) {
        return (appSectionFilter === LEFT_NAV_KEYS.UPDATES_KEY);
    }

    renderSubText(subText, app) {
        if (!subText) {
            return [];
        }

        const {
            type, localizedText, action,
        } = subText;

        const { appSectionFilter } = this.props;
        switch (type) {
        case SUBTEXT_TYPE.INFO:
            return <div className="subText-information">{localizedText}</div>;
        case SUBTEXT_TYPE.ERROR:
            return (
                <div>
                    <span className="subText-error"> {localizedText} </span>
                    { action
                          && (
                              <span>
                                  <span> - </span>
                                  <span
                                      className="subText-moreInfoLink"
                                      onClick={
                                          (e) => {
                                              e.stopPropagation();
                                              app.onAppAction(action.actionId, app);
                                              appTileAnalytics(ANALYTICS.EVENTS.MORE_INFO_CLICK, {
                                                  app,
                                                  appSectionFilter,
                                                  actionId: action.actionId,
                                              });
                                          }
                                      }>
                                      {action.localizedText}
                                  </span>
                              </span>
                          )
                    }
                </div>
            );
        default: return [];
        }
    }

    renderPlatform(platform) {
        return (
            <div className="tile-platform">
                {TranslationUtil.getMessage(`ccx.appTile.platformIcons.${platform}.tooltip`)}
            </div>
        );
    }

    renderHeaderText(app, subText, sectionHeader,
        appSectionFilter, subSectionFilter, updateTileVersion,
        isCategorySection) {
        const {
            iconURL, appFamilyName, tileVersion, sapcode, platform,
            isPDPDataAvailable, backUpIcon, codexVersion, isSupportedForLongTerm,
        } = app;
        return (
            <div
                className={classnames('listView-appTile-headerName-wrapper', { clickable: isPDPDataAvailable })}
                ariaLabel={`${appFamilyName} v ${codexVersion}`}
                role="link">
                <img
                    onClick={(e) => {
                        e.stopPropagation();
                        this.onTileHeaderClick(e);
                    }}
                    className={classnames('listView-appTile-headerName-wrapper-icon', { clickable: isPDPDataAvailable })}
                    src={iconURL}
                    onError={() => this.handleIconLoadError(app, backUpIcon)}
                    alt={appFamilyName} />
                <div className="listView-appTile-headerName-wrapper-text">
                    <div className="tile-version-wrapper">
                        <OverlayTrigger
                            disabled={!tileVersion || this.isUpdatesSection(appSectionFilter)}
                            placement="top"
                            trigger={['hover', 'focus']}
                            delayShow={500}>
                            <span
                                onClick={(e) => {
                                    e.stopPropagation();
                                    this.onTileHeaderClick(e);
                                }}
                                className={classnames('listView-appTile-primaryText', { clickable: isPDPDataAvailable })}>
                                {appFamilyName}
                            </span>
                            <Tooltip className="listView-appTile-headerName-version-tooltip">
                                <span>
                                    {
                                        isSupportedForLongTerm
                                            ? TranslationUtil.getMessage('ccx.appTile.sapCode.tooltip.version.lts', { version: tileVersion, ltsStr: TranslationUtil.getMessage('ccx.otherVersions.info.longTermSupported') })
                                            : TranslationUtil.getMessage('ccx.appTile.sapCode.tooltip.version', { version: tileVersion })
                                    }
                                </span>
                            </Tooltip>
                        </OverlayTrigger>
                        {updateTileVersion
                            && (
                                <span
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        this.onTileHeaderClick(e);
                                    }}
                                    className={classnames('listView-appTile-codexVersion', { clickable: isPDPDataAvailable })}>
                                    {TranslationUtil.getMessage('ccx.appTile.version-prefix') + updateTileVersion}
                                </span>
                            )}
                        { !updateTileVersion && app.doesMultipleVersionsExistInSection(sapcode,
                            appSectionFilter, subSectionFilter, sectionHeader)
                            && (
                                <span
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        this.onTileHeaderClick(e);
                                    }}
                                    className={classnames('listView-appTile-codexVersion', { clickable: isPDPDataAvailable })}>
                                    {TranslationUtil.getMessage('ccx.appTile.version-prefix') + tileVersion}
                                </span>
                            )
                        }
                    </div>
                    {!isCategorySection
                        ? this.renderSubText(subText, app)
                        : this.renderPlatform(platform)}
                </div>
            </div>
        );
    }

    render() {
        const {
            app,
            appSectionBasedFields,
            sectionHeader,
            appSectionFilter,
            subSectionFilter,
            renderUpdateDescription,
            renderTagline,
            appSection,
            showLabel,
        } = this.props;
        const {
            subText, updateTileVersion,
            localizedUpdateDescription = [], localizedTagline,
            isCategorySection,
        } = appSectionBasedFields;
        const { progress } = app;
        let content;

        // get header based values
        // TODO: check subSection.sectionAppsData also
        let headerBasedAppData = {};
        if (appSection && appSection.sectionAppsData && appSection.sectionAppsData[app.sapcode]) {
            headerBasedAppData = appSection.sectionAppsData[app.sapcode];
        }
        const { tagLine } = headerBasedAppData;

        if (showLabel) {
            content = null;
        } else if (localizedUpdateDescription.length) {
            content = renderUpdateDescription({
                app, appSectionBasedFields, appSectionFilter, progress,
            });
        } else {
            content = renderTagline(tagLine || localizedTagline, progress);
        }
        return (
            <>
                <div
                    className="listView-appTile-header-container"
                    onKeyDown={interpretKey({
                        onEnter: [
                            (e) => {
                                this.onTileHeaderClick(e);
                            }, true,
                        ],
                    })}>
                    <div className="listView-appTile-header">
                        {this.renderHeaderText(app, subText, sectionHeader,
                            appSectionFilter, subSectionFilter,
                            updateTileVersion, isCategorySection)}
                    </div>
                </div>
                {content}
            </>
        );
    }
}
